import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useOffline } from "@/lib/OfflineContext";
import { useTheme } from "@/lib/ThemeContext";
import { getRoleShort } from "@/lib/roles";
import { 
  LayoutDashboard, 
  FolderKanban, 
  Settings, 
  LogOut,
  ChevronLeft,
  ChevronUp,
  HardHat,
  UserCircle,
  Shield,
  ArrowLeftRight,
  FileText,
  ShieldCheck,
  WifiOff,
  RefreshCw,
  Sun,
  Moon
} from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Desktop Sidebar
const DesktopSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const navItems = [
    { icon: LayoutDashboard, label: "Tableau de bord", path: "/dashboard" },
  ];

  // Only company roles see "Projets"
  if (user?.role !== "super_admin") {
    navItems.push({ icon: FolderKanban, label: "Projets", path: "/projects" });
  }

  if (user?.role === "admin_entreprise") {
    navItems.push({ icon: Settings, label: "Administration", path: "/admin" });
  }

  if (user?.role === "super_admin") {
    navItems.push({ icon: Shield, label: "Plateforme", path: "/super-admin" });
  }

  navItems.push({ icon: UserCircle, label: "Mon compte", path: "/account" });

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const logoUrl = user?.company_logo_url
    ? `${BACKEND_URL}${user.company_logo_url}`
    : null;

  return (
    <aside className="desktop-sidebar" data-testid="desktop-sidebar">
      {/* Brand */}
      <div className="px-5 mb-8">
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-10 h-10 rounded-lg object-cover" />
          ) : (
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <HardHat size={22} className="text-white" />
            </div>
          )}
          <div>
            <p className="text-white font-bold text-base tracking-wide">SUIVI BTP</p>
            <p className="text-zinc-500 text-xs">by AVIS DOM PLUS</p>
          </div>
        </div>
      </div>

      {/* Nav links */}
      <nav className="flex-1 px-3 space-y-1">
        {navItems.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive(item.path)
                ? "bg-orange-500/15 text-orange-400"
                : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60"
            }`}
            data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <item.icon size={20} strokeWidth={1.5} />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      {/* User + logout */}
      <div className="px-3 mt-auto border-t border-zinc-800 pt-4">
        <div className="px-3 mb-3">
          <p className="text-zinc-300 text-sm truncate">{user?.first_name || user?.email}</p>
          <p className="text-zinc-500 text-xs truncate">
            {user?.company_name ? `${user.company_name} — ` : ""}{getRoleShort(user?.role)}
          </p>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-zinc-500 hover:text-red-400 hover:bg-zinc-800/60 transition-colors"
          data-testid="sidebar-logout"
        >
          <LogOut size={20} strokeWidth={1.5} />
          <span>Déconnexion</span>
        </button>
      </div>
    </aside>
  );
};

// Bottom Navigation (mobile only)
export const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const navItems = [
    { icon: LayoutDashboard, label: "Tableau", path: "/dashboard" },
  ];

  // Only company roles see "Projets"
  if (user?.role !== "super_admin") {
    navItems.push({ icon: FolderKanban, label: "Projets", path: "/projects" });
  }

  if (user?.role === "admin_entreprise") {
    navItems.push({ icon: Settings, label: "Admin", path: "/admin" });
  }

  if (user?.role === "super_admin") {
    navItems.push({ icon: Shield, label: "Plateforme", path: "/super-admin" });
  }

  navItems.push({ icon: UserCircle, label: "Compte", path: "/account" });

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <nav className="bottom-nav" data-testid="bottom-nav">
      <div className="flex items-center justify-around py-2">
        {navItems.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`flex flex-col items-center gap-1 px-3 py-2 transition-colors ${
              isActive(item.path) ? "text-orange-500" : "text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid={`nav-${item.label.toLowerCase()}`}
          >
            <item.icon size={22} strokeWidth={1.5} />
            <span className="text-xs font-medium">{item.label}</span>
          </button>
        ))}
        <button
          onClick={logout}
          className="flex flex-col items-center gap-1 px-3 py-2 text-zinc-500 hover:text-red-400 transition-colors"
          data-testid="nav-logout"
        >
          <LogOut size={22} strokeWidth={1.5} />
          <span className="text-xs font-medium">Sortir</span>
        </button>
      </div>
    </nav>
  );
};

// Page Header
export const PageHeader = ({ title, showBack = false, onBack, rightContent }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  return (
    <header className="app-header px-4 lg:px-6 py-3" data-testid="page-header">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {showBack && (
            <button
              onClick={handleBack}
              className="p-2 -ml-2 text-zinc-400 hover:text-white transition-colors"
              data-testid="back-button"
            >
              <ChevronLeft size={24} />
            </button>
          )}
          <h1 className="text-xl font-bold text-white tracking-wide">{title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-zinc-400 hover:text-orange-400 transition-colors"
            data-testid="theme-toggle-btn"
            title={theme === "dark" ? "Passer en mode jour" : "Passer en mode nuit"}
          >
            {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          {rightContent && <div>{rightContent}</div>}
        </div>
      </div>
    </header>
  );
};

// Offline status banner
const OfflineBanner = () => {
  const { isOffline, pendingCount, pendingPhotos, syncing, triggerSync } = useOffline();

  if (!isOffline && pendingCount === 0 && pendingPhotos === 0) return null;

  const totalPending = pendingCount + pendingPhotos;

  if (!isOffline && totalPending > 0) {
    // Online but pending sync
    return (
      <div className="bg-orange-600 text-white px-4 py-2 flex items-center justify-between text-sm font-medium z-[60] relative" data-testid="sync-pending-banner">
        <div className="flex items-center gap-2">
          <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
          <span>{syncing ? "Synchronisation en cours..." : `${totalPending} modification${totalPending > 1 ? "s" : ""} en attente`}</span>
        </div>
        {!syncing && (
          <button onClick={triggerSync} className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs font-bold uppercase tracking-wider transition-colors" data-testid="manual-sync-btn">
            Synchroniser
          </button>
        )}
      </div>
    );
  }

  if (isOffline) {
    return (
      <div className="bg-zinc-800 border-b border-zinc-700 text-zinc-300 px-4 py-2 flex items-center justify-between text-sm font-medium z-[60] relative" data-testid="offline-banner">
        <div className="flex items-center gap-2">
          <WifiOff size={16} className="text-red-400" />
          <span>Mode hors-ligne</span>
          {totalPending > 0 && (
            <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full text-xs font-semibold">
              {totalPending} en attente
            </span>
          )}
        </div>
        <span className="text-zinc-500 text-xs">Les modifications seront synchronisées au retour de la connexion</span>
      </div>
    );
  }

  return null;
};

// Impersonation banner
const ImpersonationBanner = () => {
  const { user, stopImpersonating } = useAuth();
  const navigate = useNavigate();

  if (!user?.is_impersonating) return null;

  const handleStop = async () => {
    const ok = await stopImpersonating();
    if (ok) navigate("/super-admin");
  };

  return (
    <div className="bg-amber-600 text-black px-4 py-2 flex items-center justify-between text-sm font-medium z-[60] relative" data-testid="impersonation-banner">
      <div className="flex items-center gap-2">
        <ArrowLeftRight size={16} />
        <span>Impersonation : <strong>{user.first_name || user.email}</strong> ({getRoleShort(user.role)})</span>
      </div>
      <button onClick={handleStop} className="bg-black/20 hover:bg-black/30 px-3 py-1 rounded text-xs font-bold uppercase tracking-wider transition-colors" data-testid="stop-impersonation">
        Revenir
      </button>
    </div>
  );
};

// Footer links
const LegalFooter = () => (
  <footer className="px-4 py-4 border-t border-zinc-800/50 mt-auto" data-testid="legal-footer">
    <div className="flex items-center justify-center gap-4 text-xs text-zinc-600">
      <Link to="/legal" className="hover:text-zinc-400 transition-colors flex items-center gap-1" data-testid="footer-legal">
        <FileText size={12} /> Mentions légales
      </Link>
      <span className="text-zinc-800">|</span>
      <Link to="/privacy" className="hover:text-zinc-400 transition-colors flex items-center gap-1" data-testid="footer-privacy">
        <ShieldCheck size={12} /> Politique de confidentialité
      </Link>
    </div>
  </footer>
);

// Scroll to top button
const ScrollToTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-24 right-4 lg:bottom-8 lg:right-8 z-40 w-10 h-10 rounded-full bg-orange-500 text-white shadow-lg flex items-center justify-center hover:bg-orange-600 transition-all animate-fade-in"
      data-testid="scroll-to-top-btn"
      title="Retour en haut"
    >
      <ChevronUp size={20} />
    </button>
  );
};

// Main Layout wrapper
export const MobileLayout = ({ children, title, showBack = false, onBack, rightContent, hideNav = false }) => {
  return (
    <>
      <OfflineBanner />
      <ImpersonationBanner />
      <DesktopSidebar />
      <div className="mobile-container">
        <PageHeader title={title} showBack={showBack} onBack={onBack} rightContent={rightContent} />
        <main className={`page-wrapper ${hideNav ? "" : "pb-20 lg:pb-8"}`}>
          {children}
        </main>
        <LegalFooter />
        <ScrollToTopButton />
        {!hideNav && <BottomNav />}
      </div>
    </>
  );
};

export default MobileLayout;
